import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge, ProgressBar, Table } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import surveyService from '../services/surveyService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SurveyResults = () => {
  const { id } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSurveyResults();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSurveyResults = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await surveyService.getSurveyResults(id);
      setResults(response.data);
    } catch (error) {
      console.error('Error loading survey results:', error);
      setError(error.response?.data?.message || 'Failed to load survey results');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getChartColors = (count) => {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    return colors.slice(0, count);
  };

  const renderQuestionResult = (questionResult, index) => {
    const question = questionResult.question;
    const questionType = question.questionType;

    return (
      <Card key={index} className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {index + 1}. {question.questionText}
            </h5>
            <Badge bg="secondary">
              {questionResult.answerCount} / {questionResult.totalResponses} responses
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          {renderQuestionChart(questionResult, questionType)}
        </Card.Body>
      </Card>
    );
  };

  const renderQuestionChart = (questionResult, questionType) => {
    switch (questionType) {
      case 'MULTIPLE_CHOICE':
      case 'SINGLE_CHOICE':
      case 'YES_NO':
        return renderChoiceResults(questionResult);
      case 'RATING':
        return renderRatingResults(questionResult);
      case 'NUMBER':
        return renderNumericResults(questionResult);
      case 'TEXT':
      case 'TEXTAREA':
      case 'EMAIL':
        return renderTextResults(questionResult);
      case 'DATE':
        return renderDateResults(questionResult);
      default:
        return <p className="text-muted">Results not available for this question type.</p>;
    }
  };

  const renderChoiceResults = (questionResult) => {
    if (!questionResult.optionResults || questionResult.optionResults.length === 0) {
      return <p className="text-muted">No responses for this question.</p>;
    }

    const chartData = {
      labels: questionResult.optionResults.map(option => option.optionText),
      datasets: [{
        data: questionResult.optionResults.map(option => option.count),
        backgroundColor: getChartColors(questionResult.optionResults.length),
        borderWidth: 1
      }]
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const option = questionResult.optionResults[context.dataIndex];
              return `${option.optionText}: ${option.count} (${option.percentage.toFixed(1)}%)`;
            }
          }
        }
      }
    };

    return (
      <Row>
        <Col md={6}>
          <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Pie data={chartData} options={chartOptions} />
          </div>
        </Col>
        <Col md={6}>
          <Table striped>
            <thead>
              <tr>
                <th>Option</th>
                <th>Count</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {questionResult.optionResults.map((option, idx) => (
                <tr key={idx}>
                  <td>{option.optionText}</td>
                  <td>{option.count}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <ProgressBar 
                        now={option.percentage} 
                        className="flex-grow-1 me-2"
                        style={{ height: '20px' }}
                      />
                      <span>{option.percentage.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    );
  };

  const renderRatingResults = (questionResult) => {
    if (!questionResult.optionResults || questionResult.optionResults.length === 0) {
      return <p className="text-muted">No responses for this question.</p>;
    }

    const chartData = {
      labels: questionResult.optionResults.map(option => option.optionText),
      datasets: [{
        label: 'Number of Responses',
        data: questionResult.optionResults.map(option => option.count),
        backgroundColor: '#36A2EB',
        borderColor: '#36A2EB',
        borderWidth: 1
      }]
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        }
      }
    };

    return (
      <div>
        <div style={{ height: '300px' }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
        <div className="mt-3">
          <Row>
            {questionResult.optionResults.map((option, idx) => (
              <Col key={idx} xs={6} md={3} className="text-center mb-2">
                <div className="border rounded p-2">
                  <div className="h4 text-primary">{option.count}</div>
                  <div className="small text-muted">Rating {option.optionText}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  };

  const renderNumericResults = (questionResult) => {
    if (!questionResult.numericResults || questionResult.numericResults.count === 0) {
      return <p className="text-muted">No numeric responses for this question.</p>;
    }

    const stats = questionResult.numericResults;

    return (
      <Row>
        <Col md={3} className="text-center">
          <div className="border rounded p-3">
            <div className="h3 text-primary">{stats.average.toFixed(2)}</div>
            <div className="text-muted">Average</div>
          </div>
        </Col>
        <Col md={3} className="text-center">
          <div className="border rounded p-3">
            <div className="h3 text-success">{stats.min}</div>
            <div className="text-muted">Minimum</div>
          </div>
        </Col>
        <Col md={3} className="text-center">
          <div className="border rounded p-3">
            <div className="h3 text-warning">{stats.max}</div>
            <div className="text-muted">Maximum</div>
          </div>
        </Col>
        <Col md={3} className="text-center">
          <div className="border rounded p-3">
            <div className="h3 text-info">{stats.count}</div>
            <div className="text-muted">Responses</div>
          </div>
        </Col>
      </Row>
    );
  };

  const renderTextResults = (questionResult) => {
    if (!questionResult.textAnswers || questionResult.textAnswers.length === 0) {
      return <p className="text-muted">No text responses for this question.</p>;
    }

    return (
      <div>
        <p className="text-muted mb-3">
          {questionResult.textAnswers.length} text response(s):
        </p>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {questionResult.textAnswers.map((answer, idx) => (
            <Card key={idx} className="mb-2">
              <Card.Body className="py-2">
                <small className="text-muted">Response {idx + 1}:</small>
                <p className="mb-0">{answer}</p>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const renderDateResults = (questionResult) => {
    if (!questionResult.dateAnswers || questionResult.dateAnswers.length === 0) {
      return <p className="text-muted">No date responses for this question.</p>;
    }

    return (
      <div>
        <p className="text-muted mb-3">
          {questionResult.dateAnswers.length} date response(s):
        </p>
        <div className="row">
          {questionResult.dateAnswers.map((date, idx) => (
            <div key={idx} className="col-md-4 mb-2">
              <div className="border rounded p-2 text-center">
                {formatDate(date)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading survey results...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Results</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-between">
            <Link to="/surveys" className="btn btn-outline-danger">
              Back to Surveys
            </Link>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!results) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>No Results Available</Alert.Heading>
          <p>Survey results are not available at this time.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>
                <i className="fas fa-chart-bar me-2 text-primary"></i>
                Survey Results
              </h1>
              <h3 className="text-muted">{results.survey.title}</h3>
            </div>
            <Link to="/surveys" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Surveys
            </Link>
          </div>
        </Col>
      </Row>

      {/* Survey Info */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              {results.survey.description && (
                <p className="text-muted mb-3">{results.survey.description}</p>
              )}
              <div className="d-flex flex-wrap gap-3">
                <div>
                  <i className="fas fa-users text-primary me-1"></i>
                  <strong>{results.totalResponses}</strong> total responses
                </div>
                <div>
                  <i className="fas fa-question-circle text-info me-1"></i>
                  <strong>{results.questionResults.length}</strong> questions
                </div>
                {results.survey.endDate && (
                  <div>
                    <i className="fas fa-calendar-times text-warning me-1"></i>
                    Ended: {formatDate(results.survey.endDate)}
                  </div>
                )}
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="text-center">
                <div className="h2 text-success">{results.totalResponses}</div>
                <div className="text-muted">Total Responses</div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Question Results */}
      {results.questionResults.length === 0 ? (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No responses have been collected for this survey yet.
        </Alert>
      ) : (
        results.questionResults.map((questionResult, index) => 
          renderQuestionResult(questionResult, index)
        )
      )}

      {/* Footer */}
      <div className="text-center mt-5 mb-4">
        <p className="text-muted">
          <i className="fas fa-chart-line me-1"></i>
          Survey results are updated in real-time
        </p>
      </div>
    </Container>
  );
};

export default SurveyResults;